/**
 * «Идеи» — every idea in one list, mine and my partner's (Kostya, 27.09: «А где
 * мне посмотреть идеи? Мои и сашкины?»). Filters answer the questions people
 * ask: what waits for my decision, my own, my partner's, everything, and what
 * is closed. A row says where the idea stands in words, never by colour alone,
 * and opens the idea screen.
 */

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";

import { Ideas, Me } from "@/api/resources";
import type { IdeaOut } from "@/api/types";
import { CaptureSheet } from "@/components/CaptureBar";
import { Button } from "@/components/ui/Button";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Mark, Status } from "@/components/ui/Status";
import { AppBar, ago, Chip, EmptyNote, genitive, ScreenHeader } from "@/kit";
import { nameOf, usePeople } from "@/lib/people";
import { useBackButton } from "@/lib/telegram-ui";

const FILTERS = ["waiting", "awaiting", "mine", "partner", "all", "closed"] as const;
type Filter = (typeof FILTERS)[number];

const OPEN: IdeaOut["status"][] = ["new", "reviewing"];
const CLOSED_MARK: Partial<Record<IdeaOut["status"], string>> = {
  converted: "стала задачей",
  approved: "принята",
  rejected: "отклонена",
  withdrawn: "отозвана автором",
};

export function ideasFor(filter: Filter, ideas: IdeaOut[], me: string | undefined): IdeaOut[] {
  const open = (i: IdeaOut) => OPEN.includes(i.status);
  switch (filter) {
    case "waiting": return ideas.filter((i) => open(i) && i.author_id !== me);
    // Mine, still open: they wait for my partner's decision (the link from «Решить»).
    case "awaiting": return ideas.filter((i) => open(i) && i.author_id === me);
    case "mine": return ideas.filter((i) => i.author_id === me);
    case "partner": return ideas.filter((i) => i.author_id !== me);
    case "closed": return ideas.filter((i) => !open(i));
    default: return ideas;
  }
}

export function IdeasScreen() {
  useBackButton("/knowledge");
  const [params, setParams] = useSearchParams();
  const [capturing, setCapturing] = useState(false);
  const people = usePeople();
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const ideas = useQuery({ queryKey: ["ideas", "all"], queryFn: () => Ideas.list() });
  const myId = me.data?.id;
  const partners = (people.data ?? []).filter((u) => u.id !== myId);
  const partnerLabel = partners.length === 1 ? `От ${genitive(partners[0].display_name)}` : "От других";

  const all = ideas.data ?? [];
  const waitingCount = ideasFor("waiting", all, myId).length;
  const filter: Filter = FILTERS.find((f) => f === params.get("f"))
    ?? (params.get("filter") === "review" || waitingCount > 0 ? "waiting" : "all");
  const shown = ideasFor(filter, all, myId);
  const choose = (f: Filter) => {
    const next = new URLSearchParams(params);
    next.set("f", f);
    setParams(next, { replace: true });
  };
  const filters: { key: Filter; label: string }[] = [
    { key: "waiting", label: "Ждут тебя" },
    { key: "awaiting", label: partners.length === 1 ? `Ждут ${genitive(partners[0].display_name)}` : "Ждут других" },
    { key: "mine", label: "Мои" },
    { key: "partner", label: partnerLabel },
    { key: "all", label: "Все" },
    { key: "closed", label: "Закрытые" },
  ];

  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/knowledge" context="Знания" />
      <ScreenHeader
        title="Идеи"
        sub={waitingCount ? `${waitingCount} ${waitingCount === 1 ? "ждёт" : "ждут"} твоего решения` : "Твоего решения ничего не ждёт"}
        trailing={<Button size="compact" variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setCapturing(true)}>Идея</Button>}
      />
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] lg:mx-0 lg:flex-wrap lg:px-0 [&::-webkit-scrollbar]:hidden">
        {filters.map((f) => (
          <Chip key={f.key} on={filter === f.key} onClick={() => choose(f.key)}>
            {f.label} <span className="font-medium">{ideasFor(f.key, all, myId).length}</span>
          </Chip>
        ))}
      </div>
      {ideas.isLoading && <SkeletonList count={4} />}
      {ideas.isError && <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void ideas.refetch()}>Повторить</Button>}>Не удалось загрузить идеи.</EmptyNote>}
      {ideas.data && shown.length === 0 && (
        <EmptyNote>{filter === "waiting" ? "Твоего решения ни одна идея не ждёт." : filter === "awaiting" ? "Твои идеи решения не ждут." : "Здесь пока пусто."}</EmptyNote>
      )}
      {shown.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {shown.map((i) => {
            const mine = i.author_id === myId;
            const open = OPEN.includes(i.status);
            const author = mine ? "твоя идея" : `идея ${genitive(nameOf(people.data, i.author_id) ?? "участника")}`;
            return (
              <li key={i.id} className="border-b border-p-border last:border-0">
                <Link to={`/knowledge/sources/idea/${i.id}`} className="flex items-start gap-3 px-4 py-3 press-surface">
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    {open && !mine && <Status tone="needs" className="self-start" />}
                    {open && mine && <Mark>ждёт решения партнёра</Mark>}
                    {!open && CLOSED_MARK[i.status] && <Mark>{CLOSED_MARK[i.status]}</Mark>}
                    <span className="text-[15px] font-semibold leading-5 text-p-text">
                      {i.summary?.trim() || i.original_text.split("\n", 1)[0].slice(0, 160)}
                    </span>
                    <span className="text-meta text-p-text-muted">{author} · {ago(i.created_at)}</span>
                  </span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      <CaptureSheet open={capturing} onClose={() => setCapturing(false)} title="Новая идея" />
    </div>
  );
}
